import { CheckIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { Tooltip } from '@/design-system';
import { useSessions } from '@/hooks/useSwr';
import { SessionSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';
import { localTime } from '@/utils/utc-time';

export const DatasetSessionCompareSelectorPopoverContent = (props: {
  selectedSessionIds: string[];
  datasetShareToken?: string;
  onlyAdd?: boolean;
  datasetName: string;
  emptyStateString?: string;
  handleSelectOrUnselectSession: (
    sessionId: string,
    shouldSelect: boolean
  ) => void;
}) => {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 250);
  const { datasetId } = useParams();
  const { data, isLoading } = useSessions(
    datasetId
      ? {
          name_contains: debouncedSearch === '' ? undefined : debouncedSearch,
          reference_dataset: [datasetId],
          include_stats: false,
        }
      : props.datasetShareToken
      ? {
          name_contains: debouncedSearch === '' ? undefined : debouncedSearch,
          include_stats: false,
        }
      : null,
    { datasetShareToken: props.datasetShareToken }
  );

  let sessionsToDisplay: SessionSchema[] = data?.rows ?? [];
  if (props.onlyAdd) {
    sessionsToDisplay = sessionsToDisplay.filter(
      (s) => !props.selectedSessionIds.includes(s.id)
    );
  }

  return (
    <Command shouldFilter={false} className="h-[400px] overflow-hidden">
      <div className="overflow-y-auto overflow-x-hidden">
        <div className="inline-flex w-full items-center justify-between py-3 pr-3">
          <div className="px-4 text-sm font-semibold leading-normal">
            Select Experiments from Dataset
          </div>
        </div>
        <div className="px-3">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder="Search Experiments..."
            className="text-sm outline-none"
          />
        </div>
        <div className="mt-3">
          <CommandList>
            <CommandGroup
              className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-heading]]:px-5 [&_[cmdk-group-items]]:py-1.5"
              heading={
                <span className="text-xs font-medium uppercase tracking-wide text-tertiary">
                  {props.datasetName}
                </span>
              }
            >
              {isLoading ? (
                <LinearProgress />
              ) : sessionsToDisplay.length > 0 ? (
                sessionsToDisplay.map((s, index) => {
                  const isSelected = props.selectedSessionIds.includes(s.id);
                  const isDisabled =
                    props.selectedSessionIds.length <= 1 && isSelected;

                  const momentCreated = localTime(s.start_time);

                  const createdString = 'Created ' + momentCreated.fromNow();

                  const commandItem = (
                    <div>
                      <DatasetSessionComparePopoverItem
                        title={s.name}
                        subtitle={createdString}
                        selected={isSelected}
                      />
                    </div>
                  );

                  return (
                    <CommandItem
                      key={index}
                      className={cn(
                        'mx-1.5 flex cursor-pointer items-center px-0 py-3',
                        isDisabled && 'cursor-default opacity-30'
                      )}
                      onSelect={() => {
                        props.handleSelectOrUnselectSession(s.id, !isSelected);
                      }}
                      disabled={isDisabled}
                    >
                      {isDisabled ? (
                        <Tooltip title="Must select at least one experiment">
                          {commandItem}
                        </Tooltip>
                      ) : (
                        commandItem
                      )}
                    </CommandItem>
                  );
                })
              ) : (
                <div className="flex h-10 items-center pt-3">
                  <DatasetSessionComparePopoverItem
                    subtitle={props.emptyStateString ?? 'No experiments found'}
                    selected={false}
                  />
                </div>
              )}
            </CommandGroup>
          </CommandList>
        </div>
      </div>
    </Command>
  );
};

const DatasetSessionComparePopoverItem = (props: {
  title?: string;
  subtitle?: string;
  selected: boolean;
}) => {
  return (
    <div
      className={
        'inline-flex w-[480px] items-center justify-between rounded-lg pl-3.5 pr-2.5'
      }
    >
      <div className="inline-flex shrink grow basis-0 flex-col items-start justify-center gap-1">
        {props.title && (
          <div className="inline-flex items-center justify-start gap-1.5 self-stretch">
            <div className="text-sm font-semibold leading-normal break-anywhere">
              {props.title}
            </div>
          </div>
        )}
        {props.subtitle && (
          <div className="inline-flex items-baseline justify-start gap-1.5">
            <div className="text-xs font-normal leading-none text-slate-400 break-anywhere">
              {props.subtitle}
            </div>
          </div>
        )}
      </div>
      {props.selected && (
        <div className="ml-3 text-brand-green-400">
          <CheckIcon className="size-4" />
        </div>
      )}
    </div>
  );
};
